import React, { useCallback, useEffect, useState } from 'react'
import apiClient from '../../services/api'
import { useToast } from '../../context/ToastContext'
import LoadingSpinner from '../../components/LoadingSpinner'

const messageFilters = [
  { key: 'all', label: 'All Messages' },
  { key: 'new', label: 'New' },
  { key: 'replied', label: 'Replied' }
]

const AdminContactMessages = () => {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [replyDrafts, setReplyDrafts] = useState({})
  const [sendingId, setSendingId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const { addToast } = useToast()

  const loadMessages = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/cms/contact-messages')
      setMessages(response.data)
    } catch (error) {
      console.error('Error loading contact messages', error)
      addToast('Error loading contact messages', 'danger')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    loadMessages()
  }, [loadMessages])

  const handleReplyChange = (id, value) => {
    setReplyDrafts((prev) => ({ ...prev, [id]: value }))
  }

  const sendReply = async (message) => {
    const replyText = (replyDrafts[message._id] ?? message.adminReply ?? '').trim()
    if (!replyText) {
      addToast('Please write a reply before sending.', 'warning')
      return
    }

    try {
      setSendingId(message._id)
      const response = await apiClient.post(`/api/cms/contact-messages/${message._id}/reply`, {
        adminReply: replyText
      })

      setMessages((prev) =>
        prev.map((item) => (item._id === message._id ? response.data : item))
      )
      setReplyDrafts((prev) => ({ ...prev, [message._id]: response.data.adminReply }))
      addToast('Reply sent successfully.', 'success')
    } catch (error) {
      addToast(error.response?.data?.message || 'Unable to send reply.', 'danger')
    } finally {
      setSendingId(null)
    }
  }

  if (loading) return <LoadingSpinner />

  const filteredMessages = messages.filter((message) => {
    if (statusFilter === 'new') return message.status !== 'replied'
    if (statusFilter === 'replied') return message.status === 'replied'
    return true
  })

  const filterCount = (key) => {
    if (key === 'new') return messages.filter((message) => message.status !== 'replied').length
    if (key === 'replied') return messages.filter((message) => message.status === 'replied').length
    return messages.length
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h2>Contact Messages</h2>
          <p>
            Filter chips and clearer status treatment make this inbox easier to manage, especially when you need to focus only on new enquiries or follow-ups.
          </p>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filter-chips">
          {messageFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`admin-filter-chip${statusFilter === filter.key ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
              <span className="admin-filter-chip-count">{filterCount(filter.key)}</span>
            </button>
          ))}
        </div>
        <div className="admin-note">{filteredMessages.length} message{filteredMessages.length === 1 ? '' : 's'} shown</div>
      </div>

      {messages.length === 0 ? (
        <div className="alert alert-info">No contact messages yet.</div>
      ) : (
        <div className="admin-card-list">
          {filteredMessages.map((message) => (
            <div key={message._id} className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                  <div>
                    <h5 className="card-title mb-1">{message.name}</h5>
                    <div className="text-muted">{message.email}</div>
                  </div>
                  <div className="text-end">
                    <span className={`admin-status-badge ${message.status === 'replied' ? 'admin-status-replied' : 'admin-status-new'}`}>
                      {message.status === 'replied' ? 'Replied' : 'New'}
                    </span>
                    <div className="small text-muted mt-2">
                      {new Date(message.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h6>Patient Message</h6>
                  <div className="border rounded p-3 bg-light">
                    {message.message}
                  </div>
                </div>

                {message.adminReply && (
                  <div className="mb-3">
                    <h6>Last Reply Sent</h6>
                    <div className="border rounded p-3">
                      {message.adminReply}
                    </div>
                    {message.repliedAt && (
                      <div className="small text-muted mt-2">
                        Sent on {new Date(message.repliedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor={`reply-${message._id}`} className="form-label">
                    Admin Reply
                  </label>
                  <textarea
                    id={`reply-${message._id}`}
                    className="form-control mb-3"
                    rows="4"
                    value={replyDrafts[message._id] ?? message.adminReply ?? ''}
                    onChange={(event) => handleReplyChange(message._id, event.target.value)}
                    placeholder="Write your reply here..."
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => sendReply(message)}
                    disabled={sendingId === message._id}
                  >
                    {sendingId === message._id ? 'Sending...' : message.status === 'replied' ? 'Send Updated Reply' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AdminContactMessages
